import { serverErrorCode, serverErrorMessage } from "@/lib/api/error-message";
import type { components } from "@/lib/api/generated/schema";

const apiBaseUrl = import.meta.env.VITE_API_URL ?? "http://localhost:8080/api/v1";

/**
 * Dựng URL tuyệt đối cho một đường dẫn công khai.
 *
 * Bản dựng cho tên miền đặt lịch riêng đặt VITE_API_URL là "/api/v1" (đường
 * dẫn TƯƠNG ĐỐI) để cookie đặt lịch/OTP luôn cùng gốc với trang. `new URL()`
 * ném lỗi với chuỗi tương đối nếu không có gốc, nên phải ghép thêm origin của
 * trình duyệt — thiếu bước này thì mọi màn đặt lịch chết ngay khi lên tên
 * miền riêng, dù ở máy dev (URL tuyệt đối) vẫn chạy ngon.
 */
export function resolvePublicBookingApiUrl(
  path: string,
  configuredBaseUrl = apiBaseUrl,
  browserOrigin = typeof window === "undefined" ? undefined : window.location.origin,
): string {
  const base = configuredBaseUrl.replace(/\/+$/, "");
  const suffix = path.startsWith("/") ? path : `/${path}`;
  return new URL(`${base}${suffix}`, browserOrigin).toString();
}

export type PublicBookingSlot = components["schemas"]["PublicCustomerBookingSlot"];
export type PublicBookingService = components["schemas"]["PublicCustomerBookingService"];
/** Một mục đích chụp của gói (Hộ chiếu, Visa, hãng bay…) — có giá và cọc riêng. */
export type PublicBookingPurpose = components["schemas"]["PublicCustomerBookingPurpose"];
/** Một kiểu ảnh của gói Profile (Tối Giản, Cổ Điển…) — kèm ảnh mẫu từ ERP. */
export type PublicBookingConcept = components["schemas"]["PublicCustomerBookingConcept"];
export type PublicBookingCreateResponse = components["schemas"]["PublicCustomerBookingCreateResponse"];
export type PublicBookingStatusResponse = components["schemas"]["PublicCustomerBookingStatusResponse"];
export type PublicCustomerMeResponse = components["schemas"]["PublicCustomerMeResponse"];
export type CustomerBooking = components["schemas"]["CustomerBooking"];
export type CustomerPortalOrder = components["schemas"]["PublicCustomerOrder"];
export type PublicBranch = components["schemas"]["PublicBranch"];
export type PublicOffpeakQuote = components["schemas"]["PublicOffpeakQuote"];
export type PublicOffpeakNearbySuggestion = components["schemas"]["PublicOffpeakNearbySuggestion"];
export type PublicBookingBillingConfig =
  components["schemas"]["PublicCustomerBookingBillingConfigResponse"];
/** `eshop` là lựa chọn hóa đơn cá nhân; `vat` là hóa đơn doanh nghiệp
 *  (bắt buộc đủ 4 thông tin bên mua). Mã API cũ được giữ để tương thích dữ liệu. */
export type CustomerBookingInvoiceType = components["schemas"]["CustomerBookingInvoiceType"];

/** Tham số đo kênh từ link — server làm sạch + hạ chữ thường source/medium. */
export type PublicUtm = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
};

export type PublicBookingCreateInput = {
  name: string;
  phone: string;
  email?: string;
  countryCallingCode: "+84";
  branchCode: string;
  serviceCode: string;
  /** Mã mục đích chụp (hộ chiếu, visa, hãng bay…). Bắt buộc khi gói có
   *  danh sách mục đích — máy chủ chốt giá từ mã này, không nhận số tiền. */
  purposeId?: string;
  /** Mã concept (kiểu ảnh) — chỉ gói Profile, KHÔNG bắt buộc. Bỏ trống thì đơn
   *  về ERP để ô Concept rỗng và ekip hỏi khách sau. */
  conceptId?: string;
  paymentOption: "deposit" | "full";
  date: string;
  startTime: string;
  clientRequestId: string;
  turnstileToken?: string;
  /** Mã ưu đãi / mã người giới thiệu khách nhập hoặc mang theo trong link. */
  promoCode?: string;
  /** Dấu máy affiliate đi kèm link từ AFF central; gửi ẩn để ghi công click. */
  affDauMay?: string;
  /** Loại hóa đơn cá nhân/doanh nghiệp. Bỏ trống = hóa đơn cá nhân. KHÔNG bật/tắt VAT — mọi lịch hẹn
   *  công khai đều chịu thuế, ô này chỉ chọn loại hóa đơn. */
  invoiceType?: CustomerBookingInvoiceType;
  /** Bốn ô dưới CHỈ gửi khi chọn `vat`; máy chủ bỏ qua và lưu NULL với eShop. */
  invoiceCompanyName?: string;
  invoiceTaxCode?: string;
  invoiceCompanyAddress?: string;
  invoiceEmail?: string;
  /** Số hóa đơn bán lẻ eShop. Tùy chọn, và chỉ có nghĩa ở nhánh `eshop` —
   *  máy chủ bỏ đi khi loại hóa đơn là `vat`. */
  invoiceEshopCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmTerm?: string;
};

export type PublicBookingErrorCode =
  | "validation"
  | "forbidden"
  | "csrf_origin_missing"
  | "csrf_origin_disallowed"
  | "slot_unavailable"
  | "active_booking_exists"
  | "version_conflict"
  | "rate_limited"
  | "service_unavailable"
  | "internal"
  | string;

export class PublicBookingError extends Error {
  code: PublicBookingErrorCode;
  status: number;
  retryAfterSeconds: number | undefined;

  constructor(message: string, code: PublicBookingErrorCode, status: number, retryAfterSeconds?: number) {
    super(message);
    this.name = "PublicBookingError";
    this.code = code;
    this.status = status;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

export async function fetchPublicAvailability(input: {
  branchCode: string;
  from: string;
  to: string;
  signal?: AbortSignal;
}): Promise<PublicBookingSlot[]> {
  const url = new URL(resolvePublicBookingApiUrl(`/public/customer-bookings/availability`));
  url.searchParams.set("branchCode", input.branchCode);
  url.searchParams.set("from", input.from);
  url.searchParams.set("to", input.to);
  const init: RequestInit = {};
  if (input.signal) init.signal = input.signal;
  const response = await fetch(url, init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không tải được lịch trống");
  const body = (await response.json()) as { slots?: PublicBookingSlot[] };
  return body.slots ?? [];
}

/**
 * Nắn giờ về đúng dạng HH:MM mà máy chủ nhận.
 *
 * Máy chủ đọc giờ bằng `time.Parse("15:04")` (customerbooking/time.go): "9:00"
 * thiếu số 0 đứng đầu và "09:00:00" kiểu TIME của Postgres đều bị đá ra 400.
 * Nguy hơn tí nữa: mã Giờ Vàng gói cả giờ vào chính nó
 * (GIOVANG-<CHINHANH>-<YYYYMMDD>-<HHMM>), nên lệch một chữ số là lệch mã.
 * Nắn một lần ở cửa gọi, thay vì bắt mọi nơi gọi nhớ hộ.
 *
 * Chuỗi không đọc được thì trả nguyên (đã cắt khoảng trắng): để máy chủ báo
 * đúng câu từ chối, chứ không tự bịa ra một giờ khác rồi hỏi giá cho ô giờ
 * chẳng ai chọn.
 */
export function normalizeClockHHMM(value: string): string {
  const trimmed = value.trim();
  const match = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(trimmed);
  if (!match) return trimmed;
  const hour = Number(match[1]);
  const minute = Number(match[2]);
  if (hour > 23 || minute > 59) return trimmed;
  return `${String(hour).padStart(2, "0")}:${match[2]}`;
}

/**
 * Xem trước giảm giá Giờ Vàng cho MỘT ô giờ.
 *
 * Ô giờ không đủ điều kiện là câu trả lời bình thường (200 kèm
 * applicable=false), không phải lỗi. Máy chủ vẫn là chốt cuối: số thật được
 * dựng lại lúc tạo lịch hẹn, đây chỉ là bản xem trước để trang không hiện một
 * đằng rồi thu một nẻo.
 */
export async function fetchPublicOffpeakQuote(input: {
  branchCode: string;
  date: string;
  startTime: string;
  serviceCode: string;
  /** Phần mã đang giảm — Giờ Vàng nhường định mức cho phần này (QĐ 0511). */
  otherDiscount?: number;
  signal?: AbortSignal;
}): Promise<PublicOffpeakQuote> {
  const url = new URL(resolvePublicBookingApiUrl(`/public/customer-bookings/offpeak-quote`));
  url.searchParams.set("branchCode", input.branchCode);
  url.searchParams.set("date", input.date);
  url.searchParams.set("startTime", normalizeClockHHMM(input.startTime));
  url.searchParams.set("serviceCode", input.serviceCode);
  if (input.otherDiscount) url.searchParams.set("otherDiscount", String(input.otherDiscount));
  const init: RequestInit = {};
  if (input.signal) init.signal = input.signal;
  const response = await fetch(url, init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không xem được ưu đãi Giờ Vàng");
  return (await response.json()) as PublicOffpeakQuote;
}

/**
 * Cơ sở CÙNG KHU VỰC đang nhả ưu đãi trong ngày đang xem (gợi ý chéo).
 *
 * Chỉ trả mã cơ sở — tên lấy từ danh sách chi nhánh trang đã tải. Không có
 * hàng xóm thì mảng rỗng, và trang không vẽ gì cả.
 */
export async function fetchPublicOffpeakNearby(input: {
  branchCode: string;
  date: string;
  signal?: AbortSignal;
}): Promise<PublicOffpeakNearbySuggestion[]> {
  const url = new URL(resolvePublicBookingApiUrl(`/public/customer-bookings/offpeak-nearby`));
  url.searchParams.set("branchCode", input.branchCode);
  url.searchParams.set("date", input.date);
  const init: RequestInit = {};
  if (input.signal) init.signal = input.signal;
  const response = await fetch(url, init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không xem được gợi ý cơ sở gần đây");
  const body = (await response.json()) as { suggestions?: PublicOffpeakNearbySuggestion[] };
  return body.suggestions ?? [];
}

export async function fetchPublicServices(
  input?: { branchCode?: string; signal?: AbortSignal },
): Promise<PublicBookingService[]> {
  const init: RequestInit = {};
  if (input?.signal) init.signal = input.signal;
  // Mã cơ sở chỉ lọc concept theo miền — giá và danh sách gói không đổi theo
  // cơ sở, nên chưa chọn cơ sở vẫn xem được đủ bảng giá.
  const query = input?.branchCode ? `?branch=${encodeURIComponent(input.branchCode)}` : "";
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-bookings/services${query}`), init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không tải được bảng giá dịch vụ");
  const body = (await response.json()) as { services?: PublicBookingService[] };
  return body.services ?? [];
}

export async function fetchPublicBranches(input?: { signal?: AbortSignal }): Promise<PublicBranch[]> {
  const init: RequestInit = {};
  if (input?.signal) init.signal = input.signal;
  // Plain fetch, not the authed client: this page has no session and the
  // Bearer interceptor would send it to the refresh flow.
  const response = await fetch(resolvePublicBookingApiUrl(`/public/branches`), init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không tải được danh sách chi nhánh");
  return (await response.json()) as PublicBranch[];
}

/**
 * Thuế suất VAT và các loại hóa đơn trang được phép chào.
 *
 * Máy chủ trả 503 khi cấu hình thuế thiếu hoặc sai — lúc đó trang phải KHÓA
 * nút đặt lịch thay vì đoán một thuế suất: đoán sai là thu sai tiền khách.
 */
export async function fetchPublicBookingBillingConfig(
  input?: { signal?: AbortSignal },
): Promise<PublicBookingBillingConfig> {
  const init: RequestInit = {};
  if (input?.signal) init.signal = input.signal;
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-bookings/billing-config`), init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không tải được cấu hình hóa đơn");
  return (await response.json()) as PublicBookingBillingConfig;
}

export async function createPublicBooking(input: PublicBookingCreateInput): Promise<PublicBookingCreateResponse> {
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-bookings`), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await publicErrorFromResponse(response, "Không gửi được yêu cầu booking");
  return (await response.json()) as PublicBookingCreateResponse;
}

/**
 * Kết quả kiểm mã ở trang công khai. `valid=false` KHÔNG phải lỗi mạng —
 * máy chủ vẫn trả 200 kèm lý do trong `message`, để khách gõ sai mã thì được
 * giải thích chứ không thấy màn lỗi.
 */
export type PublicPromoCheck = {
  valid: boolean;
  code: string;
  label: string;
  discount: number;
  message?: string;
};

export async function checkPublicPromo(input: {
  code: string;
  total: number;
  /**
   * Mã gói khách đang chọn — cho rào phạm vi áp dụng của mã (migration 0256).
   *
   * PHẢI gửi: mã có giới hạn phạm vi mà thiếu tham số này sẽ bị máy chủ từ
   * chối kèm câu "chọn gói rồi kiểm lại mã". Fail-closed như vậy là cố ý —
   * báo giảm cho một gói mã không được phép áp thì khách chuyển tiền xong đơn
   * mới bị chặn, và khoản đã hứa biến thành khoản giảm tay không ai ký.
   */
  product?: string;
  signal?: AbortSignal;
}): Promise<PublicPromoCheck> {
  const url = new URL(resolvePublicBookingApiUrl(`/public/promo-check`));
  url.searchParams.set("code", input.code);
  url.searchParams.set("total", String(input.total));
  if (input.product) url.searchParams.set("product", input.product);
  const init: RequestInit = {};
  if (input.signal) init.signal = input.signal;
  const response = await fetch(url, init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không kiểm tra được mã ưu đãi");
  return (await response.json()) as PublicPromoCheck;
}

export async function fetchPublicBookingStatus(input: {
  id: string;
  clientRequestId: string;
  signal?: AbortSignal;
}): Promise<PublicBookingStatusResponse> {
  const url = new URL(resolvePublicBookingApiUrl(`/public/customer-bookings/${input.id}/status`));
  url.searchParams.set("clientRequestId", input.clientRequestId);
  const init: RequestInit = {};
  if (input.signal) init.signal = input.signal;
  const response = await fetch(url, init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không tải được trạng thái thanh toán");
  return (await response.json()) as PublicBookingStatusResponse;
}

export async function startCustomerOTP(input: { phone: string; countryCallingCode: "+84" }) {
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-auth/otp/start`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await publicErrorFromResponse(response, "Không gửi được mã OTP");
  return (await response.json()) as components["schemas"]["PublicCustomerOTPStartResponse"];
}

export async function verifyCustomerOTP(input: { phone: string; countryCallingCode: "+84"; code: string }) {
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-auth/otp/verify`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw await publicErrorFromResponse(response, "Mã OTP không đúng hoặc đã hết hạn");
  return (await response.json()) as components["schemas"]["PublicCustomerOTPVerifyResponse"];
}

export async function fetchCustomerMe(input?: { signal?: AbortSignal }): Promise<PublicCustomerMeResponse> {
  const init: RequestInit = { credentials: "include" };
  if (input?.signal) init.signal = input.signal;
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-auth/me`), init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Bạn cần đăng nhập bằng OTP");
  return (await response.json()) as PublicCustomerMeResponse;
}

export async function logoutCustomer() {
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-auth/logout`), {
    method: "POST",
    credentials: "include",
  });
  if (!response.ok) throw await publicErrorFromResponse(response, "Không đăng xuất được");
  return response.json() as Promise<{ ok: boolean }>;
}

export async function fetchMyBookings(input?: { signal?: AbortSignal }): Promise<CustomerBooking[]> {
  const init: RequestInit = { credentials: "include" };
  if (input?.signal) init.signal = input.signal;
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-bookings/me`), init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không tải được lịch booking của bạn");
  const body = (await response.json()) as { items?: CustomerBooking[] };
  return body.items ?? [];
}

/**
 * Đơn hàng của chính khách (session SĐT) — kèm link chọn ảnh + album hoàn
 * thiện đã duyệt. BE match theo 9 số cuối nên khách nhập 090…/84… đều khớp.
 */
export async function fetchMyOrders(input?: { signal?: AbortSignal }): Promise<CustomerPortalOrder[]> {
  const init: RequestInit = { credentials: "include" };
  if (input?.signal) init.signal = input.signal;
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-orders/me`), init);
  if (!response.ok) throw await publicErrorFromResponse(response, "Không tải được đơn hàng của bạn");
  const body = (await response.json()) as { items?: CustomerPortalOrder[] };
  return body.items ?? [];
}

/**
 * Href mở link ảnh gốc thứ `i` của đơn — trỏ về máy chủ, KHÔNG phải link kho.
 *
 * Máy chủ ghi sổ `raw_link_views` rồi mới 302 (quyết định 0061). Đó là lý do
 * không có hàm nào trả mảng URL gốc: cầm được URL là mở kho mãi mãi không ai
 * biết. Số nút dựng từ `order.rawLinkCount`; thẻ <a> mang cookie phiên khách
 * (SameSite=Lax) nên mở tab mới vẫn còn phiên — không cần fetch tay.
 */
export function customerRawLinkHref(orderId: string, i: number): string {
  return resolvePublicBookingApiUrl(
    `/public/customer-orders/${encodeURIComponent(orderId)}/raw-link?i=${i}`,
  );
}

export async function requestMyBookingReschedule(input: {
  id: string;
  shootDate: string;
  startTime: string;
  note?: string;
}): Promise<CustomerBooking> {
  const response = await fetch(resolvePublicBookingApiUrl(`/public/customer-bookings/${input.id}/request-reschedule`), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ shootDate: input.shootDate, startTime: input.startTime, note: input.note ?? "" }),
  });
  if (!response.ok) throw await publicErrorFromResponse(response, "Không gửi được yêu cầu đổi lịch");
  return (await response.json()) as CustomerBooking;
}

async function publicErrorFromResponse(response: Response, fallback: string): Promise<PublicBookingError> {
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    // Keep fallback.
  }
  const code = serverErrorCode(body) || (response.status === 429 ? "rate_limited" : "unknown");
  const retryAfter = Number(response.headers.get("Retry-After") ?? "");
  return new PublicBookingError(
    serverErrorMessage(body, fallback),
    code,
    response.status,
    Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
  );
}

/**
 * publicBookingMessage — đổi lỗi kỹ thuật thành câu khách đọc được.
 *
 * Đặt ở đây (thay vì trong một trang) vì cả trang đặt lịch, trang xem đơn và
 * form đổi lịch đều cần đúng một bộ câu chữ: khách gặp cùng một sự cố ở hai
 * chỗ mà đọc hai câu khác nhau là tưởng hai lỗi khác nhau.
 */
export function publicBookingMessage(error: unknown): string {
  if (error instanceof PublicBookingError) {
    if (error.code === "service_unavailable") return "OTP chưa được cấu hình hoặc kênh này chưa bật. Bạn vui lòng liên hệ CSKH.";
    if (error.code === "unauthorized") return "Mã OTP không đúng hoặc phiên đăng nhập đã hết hạn.";
    if (error.code === "rate_limited") return "Bạn gửi OTP hơi nhanh. Vui lòng thử lại sau.";
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return "Không xử lý được yêu cầu.";
}
